import { useCallback, useState } from "react";
import { Button, HStack, IconButton, Tooltip } from "@chakra-ui/react";
import { ChevronRightIcon, RepeatIcon } from "@chakra-ui/icons";

import { getStoredEmail, logoutSession } from "../auth/auth";

const COLLAPSED_KEY = "refresh_button_collapsed";

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    // приватный режим или заблокированное хранилище — показываем целиком
    return false;
  }
}

function rememberCollapsed(collapsed) {
  try {
    if (collapsed) localStorage.setItem(COLLAPSED_KEY, "1");
    else localStorage.removeItem(COLLAPSED_KEY);
  } catch {
    // не критично: состояние просто не переживёт перезагрузку
  }
}

/**
 * Кнопка «Обновить сессию»: сбрасывает вход и открывает страницу входа заново.
 *
 * Живёт выше роутера (см. App.js): у защищённых маршрутов есть loader, и пока
 * он не завершился, страница не рендерится. Если дашборд завис именно там,
 * кнопка внутри страницы не появилась бы — то есть была бы бесполезна ровно
 * в том случае, ради которого её и делают.
 *
 * Сворачивается в маленькую круглую кнопку, чтобы не закрывать данные отчёта.
 * Состояние запоминается, поэтому свёрнутой она остаётся и после перезагрузки.
 */
const RefreshSessionButton = () => {
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const setCollapsedState = useCallback(value => {
    setCollapsed(value);
    rememberCollapsed(value);
  }, []);

  const handleClick = useCallback(async () => {
    if (busy) return;
    setBusy(true);

    // Почта — не секрет, и её сохранение избавляет от повторного ввода.
    let email = null;
    try {
      email = getStoredEmail();
    } catch {
      // приватный режим или заблокированное хранилище — не критично
    }

    try {
      // Гасим refresh-токен текущего устройства на сервере. Другие устройства
      // это не затрагивает. Ждём не дольше 5 секунд: если сеть висит, всё
      // равно уходим на страницу входа.
      await Promise.race([
        logoutSession(),
        new Promise(resolve => setTimeout(resolve, 5000)),
      ]);
    } catch {
      // Сервер недоступен — локальную часть всё равно чистим ниже.
    }

    try {
      sessionStorage.clear();
      localStorage.removeItem("refresh_token");
      if (email) localStorage.setItem("email", email);
    } catch {
      // хранилище недоступно — переход на вход всё равно выполняем
    }

    // replace, а не href: кнопкой «назад» не вернуться в сломанное состояние.
    // Параметр в адресе даёт браузеру свежий документ вместо кэша страницы.
    window.location.replace(`/?relogin=${Date.now()}`);
  }, [busy]);

  if (collapsed) {
    return (
      <Tooltip label="Развернуть кнопку обновления сессии" placement="left" openDelay={400} hasArrow>
        <IconButton
          aria-label="Развернуть кнопку обновления сессии"
          icon={<RepeatIcon />}
          onClick={() => setCollapsedState(false)}
          size="sm"
          colorScheme="orange"
          isRound
          position="fixed"
          bottom="16px"
          right="16px"
          zIndex={2000}
          boxShadow="lg"
        />
      </Tooltip>
    );
  }

  return (
    <HStack
      spacing={0}
      position="fixed"
      bottom="16px"
      right="16px"
      zIndex={2000}
      boxShadow="lg"
      borderRadius="md"
      overflow="hidden"
    >
      <Tooltip
        label="Выйти и открыть страницу входа заново. Помогает, если дашборд завис или не пускает после долгого перерыва"
        placement="left"
        openDelay={400}
        hasArrow
      >
        <Button
          onClick={handleClick}
          isLoading={busy}
          loadingText="Выходим"
          leftIcon={<RepeatIcon />}
          size="sm"
          colorScheme="orange"
          borderRadius={0}
        >
          Обновить сессию
        </Button>
      </Tooltip>
      <Tooltip label="Свернуть" placement="top" openDelay={400} hasArrow>
        <IconButton
          aria-label="Свернуть кнопку обновления сессии"
          icon={<ChevronRightIcon />}
          onClick={() => setCollapsedState(true)}
          size="sm"
          colorScheme="orange"
          borderRadius={0}
          // Отделяем от основного действия, чтобы не свернуть вместо выхода.
          borderLeft="1px solid rgba(0, 0, 0, 0.25)"
        />
      </Tooltip>
    </HStack>
  );
};

export default RefreshSessionButton;
